import { useState, useRef, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import { Camera, X, Check, ZoomIn, RotateCw, Image as ImageIcon } from 'lucide-react'
import type { Area } from 'react-easy-crop'
import { getCroppedImg } from '../utils/cropImage'
import imageCompression from 'browser-image-compression'

interface AvatarUploadFieldProps {
    value?: string | File
    onChange: (file: File) => void
    error?: string
}

export function AvatarUploadField({ value, onChange, error }: AvatarUploadFieldProps) {
    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [rotation, setRotation] = useState(0)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
    const [isCropping, setIsCropping] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0]
            const imageDataUrl = await readFile(file)
            setImageSrc(imageDataUrl)
            setIsCropping(true)
            // Reset input value to allow selecting same file again
            e.target.value = ''
        }
    }

    const readFile = (file: File) => {
        return new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.addEventListener('load', () => resolve(reader.result as string))
            reader.readAsDataURL(file)
        })
    }

    const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }, [])

    const handleSaveCrop = async () => {
        try {
            if (!imageSrc || !croppedAreaPixels) return

            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation)

            if (!croppedImageBlob) return

            // Compress image
            const options = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 512,
                useWebWorker: true,
                fileType: 'image/webp'
            }

            const compressedFile = await imageCompression(croppedImageBlob as File, options)

            // Convert Blob to File with proper name
            const fileWithName = new File([compressedFile], 'avatar.webp', { type: 'image/webp' })

            // Create preview URL
            const url = URL.createObjectURL(fileWithName)
            setPreviewUrl(url)

            // Pass file to parent
            onChange(fileWithName)

            setIsCropping(false)
        } catch (e) {
            console.error(e)
        }
    }

    const handleCancelCrop = () => {
        setIsCropping(false)
        setImageSrc(null)
    }

    const triggerFileSelect = () => {
        fileInputRef.current?.click()
    }

    // Determine what to show as preview
    const displayImage = previewUrl || (typeof value === 'string' ? value : null)

    return (
        <div className="flex flex-col items-center gap-4">
            <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                ref={fileInputRef}
                className="hidden"
            />

            {/* Avatar Circle */}
            <div
                onClick={triggerFileSelect}
                className={`
                    relative w-32 h-32 rounded-full cursor-pointer group transition-all duration-300
                    ${error ? 'ring-2 ring-red-500' : 'hover:ring-4 ring-purple-500/20'}
                `}
            >
                {/* Gradient Border Container */}
                <div className="absolute inset-0 rounded-full p-[2px] bg-gradient-to-br from-blue-400 via-purple-500 to-pink-400 opacity-70 group-hover:opacity-100 transition-opacity">
                    <div className="w-full h-full rounded-full bg-white dark:bg-gray-900 overflow-hidden relative">
                        {displayImage ? (
                            <img
                                src={displayImage}
                                alt="Avatar Preview"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800 text-gray-400">
                                <Camera className="w-10 h-10" />
                            </div>
                        )}

                        {/* Overlay on Hover */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs font-medium flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Alterar
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {error && <p className="text-red-500 text-xs">{error}</p>}

            {/* Cropping Modal */}
            {isCropping && imageSrc && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900 dark:text-white">Ajustar Imagem</h3>
                            <button onClick={handleCancelCrop} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="relative h-80 w-full bg-black">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                                cropShape="round"
                                showGrid={false}
                            />
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Controls */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <ZoomIn className="w-4 h-4 text-gray-500" />
                                    <input
                                        type="range"
                                        value={zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        aria-labelledby="Zoom"
                                        onChange={(e) => setZoom(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-purple-600"
                                    />
                                </div>
                                <div className="flex items-center gap-3">
                                    <RotateCw className="w-4 h-4 text-gray-500" />
                                    <input
                                        type="range"
                                        value={rotation}
                                        min={0}
                                        max={360}
                                        step={1}
                                        aria-labelledby="Rotation"
                                        onChange={(e) => setRotation(Number(e.target.value))}
                                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-purple-600"
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleCancelCrop}
                                    className="flex-1 py-2.5 px-4 border border-gray-300 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveCrop}
                                    className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl text-sm font-medium shadow-lg shadow-purple-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <Check className="w-4 h-4" />
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
